import { useMemo, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { QrCode, Copy, Printer, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Modal that renders a QR code pointing to the public /careers portal.
 * Provides Copy Link, Download PNG and Print (poster) actions so an admin
 * can share the portal at the mine site.
 */
export default function CareersQrDialog({ open, onOpenChange }) {
  const url = useMemo(() => `${window.location.origin}/careers`, []);
  const wrapRef = useRef(null);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace del portal copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const downloadPng = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "qr-portal-empleo-ug-oomining.png";
    a.click();
  };

  const printPoster = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) { toast.error("Habilita ventanas emergentes para imprimir"); return; }
    win.document.write(`<!doctype html><html><head><title>Portal de Empleo — UG Managment by OOMINING</title>
      <style>
        body{font-family:Inter,Arial,sans-serif;margin:0;padding:48px;background:#0c183c;color:#fff;text-align:center}
        h1{font-size:34px;margin:0 0 8px;font-weight:900;letter-spacing:-.02em}
        .sub{color:#93c5fd;text-transform:uppercase;letter-spacing:.25em;font-size:11px;font-weight:800;margin-bottom:32px}
        .card{background:#fff;color:#0f172a;border-radius:16px;padding:36px;max-width:520px;margin:0 auto;box-shadow:0 20px 60px rgba(0,0,0,.35)}
        .card h2{font-size:22px;margin:0 0 6px}
        .card p{color:#475569;font-size:13px;margin:0 0 20px}
        .card img{max-width:100%;height:auto}
        .url{margin-top:16px;font-size:12px;color:#334155;word-break:break-all}
        .badge{display:inline-block;background:#fbbf24;color:#0c183c;padding:6px 14px;border-radius:999px;font-weight:800;font-size:11px;letter-spacing:.15em;text-transform:uppercase;margin-bottom:14px}
      </style></head><body>
      <span class="badge">Estamos contratando</span>
      <h1>Trabaja con Nosotros</h1>
      <div class="sub">UG Managment by OOMINING</div>
      <div class="card">
        <h2>Escanea y postula</h2>
        <p>Apunta la cámara de tu celular al código QR para ver todas las vacantes abiertas y enviar tu CV.</p>
        <img src="${dataUrl}" alt="QR Portal de Empleo" />
        <div class="url">${url}</div>
      </div>
      <script>setTimeout(()=>window.print(),400);</script>
      </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="careers-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <QrCode size={18} weight="bold" className="text-indigo-700" /> Portal de Empleo — QR
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">
          Comparte este código en avisos, tótems o publicaciones. Los candidatos escanean, ven las vacantes abiertas y postulan
          adjuntando su CV en PDF o imagen — sin registrarse.
        </p>
        <div ref={wrapRef} className="flex justify-center py-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="careers-qr-canvas">
          <QRCodeCanvas
            value={url}
            size={220}
            level="M"
            includeMargin
            bgColor="#ffffff"
            fgColor="#0c183c"
          />
        </div>
        <div className="text-[11px] text-slate-500 text-center break-all font-mono">{url}</div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink} data-testid="careers-qr-copy">
            <Copy size={13} weight="bold" /> Copiar
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadPng} data-testid="careers-qr-download">
            <DownloadSimple size={13} weight="bold" /> PNG
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={printPoster} data-testid="careers-qr-print">
            <Printer size={13} weight="bold" /> Imprimir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
